import {
  Fullscreen,
  FullscreenExit,
  Pause,
  PlayArrow,
  Replay30,
  SkipNext,
  SkipPrevious,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Hidden,
  IconButton,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import screenfull from 'screenfull';

import { PLAYER_REPLAY_30 } from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  playerActions,
  playerThunks,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsPlayerBuffering,
  selectIsPlayerPlaying,
  selectIsPlayerSeeking,
  selectIsPlayerVideoFullscreen,
  selectPlayerProgress,
  selectPlayerSeekToPosition,
  useAppDispatch,
  useAppSelector,
  useFetchChapterQuery,
  useFetchEpisodeChaptersQuery,
  useFetchEpisodeQuery,
} from '@/store';
import { convertMsToHHMMSS } from '@/utils';

import { ProgressBar } from '../../../ProgressBar';
import { VolumeSlider } from '../../../VolumeSlider';

const VISIBILITY_DURATION = 3500;

export const VideoControls = () => {
  const dispatch = useAppDispatch();

  const [visible, setVisible] = useState(false);
  const lastChapterId = useRef<string>(null);
  const visibilityTimer = useRef<NodeJS.Timeout>(null);
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId)!;
  const activeChapterId = useAppSelector(selectActiveChapterId)!;
  const isBuffering = useAppSelector(selectIsPlayerBuffering);
  const isPlaying = useAppSelector(selectIsPlayerPlaying);
  const isSeeking = useAppSelector(selectIsPlayerSeeking);
  const seekToPosition = useAppSelector(selectPlayerSeekToPosition);
  const progress = useAppSelector(selectPlayerProgress);
  const isVideoFullscreen = useAppSelector(selectIsPlayerVideoFullscreen);
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );
  const location = { component: EVENT_CONSTANTS.COMPONENT.VIDEO_PLAYER };

  const { data: episode } = useFetchEpisodeQuery({
    episodeId: activeEpisodeId,
    compliance,
  });
  const { data: chapters } = useFetchEpisodeChaptersQuery({
    episodeId: activeEpisodeId,
    compliance,
  });
  const chapterPosition =
    chapters?.find(({ chapterId }) => chapterId === activeChapterId)
      ?.chapterPosition ?? -1;
  const nextChapter = chapters?.find(
    current => current.chapterPosition === chapterPosition + 1,
  );
  const previousChapter = chapters?.find(
    current => current.chapterPosition === chapterPosition - 1,
  );
  const isChapterTrailer = activeChapterId === episode?.trailerId;
  const { data: chapter } = useFetchChapterQuery({
    chapterId: activeChapterId,
    compliance,
  });
  const chapterDuration = chapter?.duration || 0;

  const toggleFullscreen = () => {
    dispatch(playerActions.setVideoFullscreen(!isVideoFullscreen));
    AnalyticsService.videoPlayerFullScreen({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      isFullScreen: !isVideoFullscreen,
    });
  };

  const togglePlay = () => {
    dispatch(
      isPlaying ? playerThunks.pausePlayer() : playerThunks.playPlayer(),
    );

    if (chapter) {
      AnalyticsService.chapterPause({
        chapterId: activeChapterId,
        episodeId: activeEpisodeId,
        location: {
          ...location,
          button: EVENT_CONSTANTS.BUTTON.PLAY,
        },
        referrer: location,
        pausing: isPlaying,
      });
    }
  };

  const seekThirtySecondsBack = () => {
    dispatch(playerThunks.seekToPosition({ delta: PLAYER_REPLAY_30 }));
    AnalyticsService.trackPlayerRewind({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      location: { ...location, button: EVENT_CONSTANTS.BUTTON.SEEK_THIRTY },
    });
  };

  const playNext = () => {
    if (nextChapter) {
      dispatch(
        playerThunks.chapterPlayPressed({
          activeChapter: {
            episodeId: nextChapter.episodeId,
            chapterId: nextChapter.chapterId,
          },
          location: { ...location, button: EVENT_CONSTANTS.BUTTON.SKIP_NEXT },
          referrer: location,
        }),
      );
    }
  };
  const playPrevious = () => {
    if (previousChapter) {
      dispatch(
        playerThunks.chapterPlayPressed({
          activeChapter: {
            episodeId: previousChapter.episodeId,
            chapterId: previousChapter.chapterId,
          },
          location: {
            ...location,
            button: EVENT_CONSTANTS.BUTTON.SKIP_PREVIOUS,
          },
          referrer: location,
        }),
      );
    }
  };

  const onMouseMove = useCallback(() => {
    if (visibilityTimer.current) {
      clearTimeout(visibilityTimer.current);
    }
    if (!visible) {
      setVisible(true);
    }
    visibilityTimer.current = setTimeout(
      () => setVisible(false),
      VISIBILITY_DURATION,
    );
  }, [visible]);

  const onMouseLeave = useCallback(() => {
    if (visibilityTimer.current) {
      clearTimeout(visibilityTimer.current);
    }
    if (visible) {
      setVisible(false);
    }
  }, [visible]);

  useEffect(
    () => () => {
      if (visibilityTimer.current) clearTimeout(visibilityTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (
      activeChapterId !== lastChapterId.current &&
      !isBuffering &&
      isPlaying
    ) {
      lastChapterId.current = activeChapterId;
      onMouseMove();
    }
  }, [activeChapterId, isBuffering, isPlaying, onMouseMove]);

  const position = progress.position || seekToPosition || 0;
  const formattedCurrentPosition = convertMsToHHMMSS(position * 1000);
  const formattedChapterDuration = convertMsToHHMMSS(chapterDuration * 1000);

  if (isBuffering) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%">
        <CircularProgress sx={styles.circularProgressIndicator} />
      </Box>
    );
  }

  return (
    <Box
      sx={[styles.overlay, visible && styles.overlayVisible]}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onTouchStart={
        onMouseMove
      } /* required for touch devices since mouse events might not work */
    >
      <Box sx={styles.controlsContainer}>
        <Box sx={styles.controls}>
          <IconButton
            sx={styles.button}
            onClick={seekThirtySecondsBack}
            disabled={isSeeking}
            disableRipple={true}
            disableFocusRipple={true}
            aria-label="Back 30 seconds">
            <Replay30 sx={styles.buttonIcon} />
          </IconButton>
          <Hidden xsDown>
            <IconButton
              sx={styles.button}
              onClick={playPrevious}
              disabled={isChapterTrailer || !previousChapter}
              disableRipple={true}
              disableFocusRipple={true}
              aria-label="Previous">
              <SkipPrevious sx={styles.buttonIcon} />
            </IconButton>
          </Hidden>
          <IconButton
            sx={styles.button}
            onClick={togglePlay}
            disabled={!activeChapterId}
            disableRipple={true}
            disableFocusRipple={true}
            aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? (
              <Pause sx={styles.buttonIcon} />
            ) : (
              <PlayArrow sx={styles.buttonIcon} />
            )}
          </IconButton>
          <Hidden xsDown>
            <IconButton
              sx={styles.button}
              onClick={playNext}
              disabled={isChapterTrailer || !nextChapter}
              disableRipple={true}
              disableFocusRipple={true}
              aria-label="Next">
              <SkipNext sx={styles.buttonIcon} />
            </IconButton>
          </Hidden>
          {progress && (
            <Box sx={styles.progressBar}>
              <ProgressBar referrer={location} />
            </Box>
          )}
          {progress && (
            <Typography variant="caption1" sx={{ color: 'text.contrast' }}>
              {formattedCurrentPosition} / {formattedChapterDuration}
            </Typography>
          )}
          <Hidden mdDown>
            <Box sx={styles.volumeControls}>
              <VolumeSlider iconSize={26} referrer={location} />
            </Box>
          </Hidden>
          {screenfull.isEnabled && (
            <IconButton
              sx={styles.button}
              onClick={toggleFullscreen}
              disabled={!activeChapterId}
              disableRipple={true}
              disableFocusRipple={true}
              aria-label="Toggle fullscreen"
              aria-haspopup="true"
              aria-expanded={isVideoFullscreen}>
              {isVideoFullscreen ? (
                <FullscreenExit sx={styles.buttonIcon} />
              ) : (
                <Fullscreen sx={styles.buttonIcon} />
              )}
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  overlay: {
    cursor: 'auto',
    height: '100%',
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    width: '100%',
  },
  overlayVisible: {
    opacity: 1,
  },
  controlsContainer: {
    bottom: 0,
    position: 'absolute',
    width: '100%',
  },
  controls: {
    alignItems: 'center',
    backgroundColor: 'grey175',
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'row',
    height: 38,
    justifyContent: 'space-between',
    margin: 2,
    opacity: 0.8,
  },
  button: {
    color: 'white',
    p: 1,
  },
  buttonIcon: {
    fontSize: 26,
  },
  progressBar: {
    flex: 1,
    m: 1,
  },
  volumeControls: {
    p: 1,
  },
  circularProgressIndicator: {
    fontSize: 32,
    color: 'grey175',
  },
};
